/**
 * useMediaEditor
 * ──────────────────────────────────────────────────────────────────────────────
 * Hook para controlar abertura/fechamento do MediaEditor.
 *
 * Uso:
 *   const editor = useMediaEditor();
 *
 *   // Para abrir:
 *   editor.open(file, {
 *     onConfirm: (editedFile) => upload(editedFile),
 *   });
 *
 *   // No JSX:
 *   <MediaEditor {...editor.props} />
 * ──────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useState } from "react";
import { MediaEditorProps } from "../components/media-editor";

interface OpenOptions
  extends Omit<MediaEditorProps, "file" | "onConfirm" | "onCancel"> {
  onConfirm: (file: File) => void | Promise<void>;
  onCancel?: () => void;
}

interface UseMediaEditorReturn {
  /** true quando o editor deve estar visível */
  isOpen: boolean;
  /** Props prontas para passar ao <MediaEditor> */
  props: MediaEditorProps;
  /** Abre o editor com o arquivo e opções fornecidos */
  open: (file: File, options: OpenOptions) => void;
  /** Fecha o editor programaticamente */
  close: () => void;
}

export function useMediaEditor(): UseMediaEditorReturn {
  const [file,    setFile]    = useState<File | null>(null);
  const [options, setOptions] = useState<OpenOptions | null>(null);
  const [isOpen,  setIsOpen]  = useState(false);

  const open = useCallback((f: File, opts: OpenOptions) => {
    setFile(f);
    setOptions(opts);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Mantém file/options para evitar flash durante animação de saída
    setTimeout(() => { setFile(null); setOptions(null); }, 300);
  }, []);

  const handleConfirm = useCallback(async (edited: File) => {
    await options?.onConfirm(edited);
    close();
  }, [options, close]);

  const handleCancel = useCallback(() => {
    options?.onCancel?.();
    close();
  }, [options, close]);

  return {
    isOpen,
    open,
    close,
    props: {
      file,
      onConfirm: handleConfirm,
      onCancel:  handleCancel,
      // espalha options MAS remove onConfirm/onCancel para não sobrescrever os handlers do hook
      ...Object.fromEntries(
        Object.entries(options ?? {}).filter(
          ([k]) => k !== "onConfirm" && k !== "onCancel"
        )
      ),
    } as MediaEditorProps,
  };
}